import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { labelsColors, Rect, RectType } from "../../models/Rect";
import { Dialog } from "../dialog/Dialog";
import { ImageCanvas } from "./ImageCanvas";
import { Box, FormControlLabel, Grid, Switch, TextField } from "@mui/material";
import Button from "../button/Button";
import { useTheme } from "../../services/theme-service/ThemeService";
import { translationService } from "../../services/translation/TranslationService";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import dateService from "../../services/date-service/DateService";
import LabelingInfo from "./LabelingInfo";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from "dayjs";
import { OcrService } from "../../services/ocr/OcrService";

type Mode = 'current' | 'new';

type Props = {
  isVisible: boolean;
  rectTypes: RectType[];
  onClose: () => void;
  onComplete: (setMainInfo: boolean, mode: Mode) => void;
  screenshot: string | null;
  rects: Map<RectType, Rect>;
  setRects: (rects: Map<RectType, Rect>) => void;
  ocrService: OcrService;
  showToggle?: boolean;
  // Whether this dialog is a read-only replay of saved labels. The caller decides — it is
  // NOT the same as ocrService.ocrSuccessful: during a Redo the previous result is still
  // loaded (so ocrSuccessful is true) while the labels are being re-drawn for the NEXT run.
  // Reading the flag directly here left the end-time picker and the canvas disabled through
  // the whole redo. Defaults to ocrSuccessful for callers that don't pass it.
  readOnly?: boolean;
};

export const LabelingDialog: React.FC<Props> = observer(
  ({ isVisible, rectTypes, onClose, onComplete, screenshot, rects, setRects, ocrService, showToggle = false, readOnly }) => {
    const { themeClass } = useTheme();
    const isReadOnly = readOnly ?? ocrService.ocrSuccessful;
    const [rectsInput, setRectsInput] = useState<Map<RectType, string>>(new Map());
    const [rectType, setRectType] = useState<RectType | null>(null);
    const [detectMainInfo, setDetectMainInfo] = useState(true);
    const [mode, setMode] = useState<Mode>('current');
    const showingSavedFrame = showToggle ? mode === 'current' : isReadOnly;
    const detectionTime = showingSavedFrame && rectTypes.length > 0
      ? dateService.formatTime(ocrService.dialogs[rectTypes[0]].timestamp)
      : dateService.formatTime(videoPlayerService.lastScreenshotTimestamp);

    const maxTime: number = (videoPlayerService.videoDuration);
    const startOfDay = dayjs().startOf('day');
    const defaultEndTime = startOfDay.add(maxTime, 'second');
    const [endTimeValue, setEndTimeValue] = useState<Dayjs | null>(null);
    
    const isDialogOpened = useRef(false);
    useEffect(() => {
      if (isVisible && !isDialogOpened.current) {
        const timeToUse = ocrService.userEndTime || maxTime;
        const endTime = startOfDay.add(timeToUse, 'second');
        setEndTimeValue(endTime);
        setMode('current');
        isDialogOpened.current = true;
      } else if (!isVisible && isDialogOpened.current) {
        isDialogOpened.current = false;
      }
    }, [isVisible, maxTime, startOfDay, ocrService.userEndTime]);

    const handleModeChange = async (newMode: Mode) => {
      if (newMode === mode) return;
      if (newMode === 'new') {
        await videoPlayerService.takeScreenshotAtCurrentVideoPosition(ocrService.fileId!);
        ocrService.setScreenshotUrl(videoPlayerService.lastScreenshotUri);
        setRects(new Map());
      } else {
        ocrService.setScreenshotUrl(ocrService.dialogs[rectTypes[0]].screenshot);
        setRects(ocrService.getRects(rectTypes));
      }
      setMode(newMode);
    };

    useEffect(() => {
      let rectsInputCopy = new Map(rectsInput);
      rects.forEach((rect,key) => {
        const x = Math.round(rect!.x);
        const y = Math.round(rect!.y);
        const w = Math.round(rect!.w);
        const h = Math.round(rect!.h);
        rectsInputCopy.set(key, `${x},${y},${w},${h}`); 
      });
      rectsInput.forEach((_,key) => {
      if(!rects.has(key)) rectsInputCopy.delete(key); 
    });
      setRectsInput(rectsInputCopy);
    }, [rects]);
    useEffect(() => {
      setRectType(rectTypes[0]);
    }, [rectTypes]);
    const isEndTimeValid = endTimeValue !== null && 
                          endTimeValue.diff(startOfDay, 'second') > 0 && 
                          endTimeValue.diff(startOfDay, 'second') <= maxTime;

    // The end time belongs to the INSPECTION, not to the captured frame: 'current' only
    // means "keep the saved frame and its regions". Saving it only in 'new' mode meant OK
    // silently dropped it on every re-open of an already-labeled file — while the start
    // time still went through, because handleComplete writes that from the fresh capture.
    // Only the detections dialog owns the field (it is the one that renders the picker).
    const ownsEndTime = rectTypes[0] === "detections";

    const saveChanges = () => {
      const finalMode: Mode = showToggle ? mode : 'new';
      if (ownsEndTime && !isReadOnly) {
        ocrService.setUserEndTime(endTimeValue);
      }
      onComplete(detectMainInfo, finalMode);
    };

    if (rectTypes.length === 0 || !screenshot) return null;

    const modeToggle = showToggle ? (
      <Box
        className="buttons-container"
        sx={{
          display: 'flex',
          borderRadius: '17.5px',
          border: 'solid 1px',
          borderColor: 'grey.400',
          padding: '2px',
          '& .fill-color-ghost-light, & .fill-color-ghost-dark': {
            border: 'none',
          },
          '& button': {
            borderRadius: '13px',
          },
        }}
      >
        <Button
          textKey="ocrService.dialogContent.toggle.current"
          onClick={() => handleModeChange('current')}
          className={themeClass(["btn-big", mode === 'current' ? "" : "fill-color-ghost"])}
        />
        <Button
          textKey="ocrService.dialogContent.toggle.new"
          onClick={() => handleModeChange('new')}
          className={themeClass(["btn-big", mode === 'new' ? "" : "fill-color-ghost"])}
        />
      </Box>
    ) : undefined;

    return (
      <Dialog
        titleKey={`ocrService.dialogTitle.${rectTypes[0]}`}
        isVisible={isVisible}
        subTextKey={`ocrService.dialogSubText.${rectTypes[0]}`}
        onHide={onClose}
        maxWidth="md"
        paperSx={{ maxWidth: "855px" }}
        buttonOk={{
          textKey: "general.ok",
          action: saveChanges,
          disabled: !isEndTimeValid,
        }}
        actionExtras={modeToggle}
      >
        <Grid container xs={12} spacing={2}>
          <Grid item xs={12}>
            {rectTypes[0] === "main" && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={detectMainInfo}
                      onChange={(e) => setDetectMainInfo(e.target.checked)}
                      disabled={isReadOnly || showingSavedFrame}
                    />
                  }
                  label={translationService.translate(
                    `ocrService.dialogContent.${rectTypes[0]}.toggle`
                  )}
                  sx={{ mr: 4 }}
                />
              </>
            )}
          </Grid>
          {rectTypes[0] === "detections" ? (
            <Grid item xs={11} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 32, flexShrink: 0 }} />
              <TextField
                variant="outlined"
                size="small"
                value={detectionTime}
                InputProps={{ readOnly: true }}
                label={translationService.translate(`ocrService.dialogContent.${rectTypes[0]}.time`)}
                sx={{ width: 250 }}
              />
              <Box sx={{ width: 32, flexShrink: 0 }} />
              <Box sx={{ width: 32, flexShrink: 0 }} />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  views={['minutes', 'seconds']}
                  format="mm:ss"
                  slotProps={{ textField: { size: "small", sx: { width: 250 } } }}
                  value={endTimeValue}
                  onChange={(newValue) => setEndTimeValue(newValue)}
                  minTime={startOfDay.add(1, 'second')}
                  maxTime={defaultEndTime}
                  referenceDate={startOfDay}
                  timeSteps={{ minutes: 1, seconds: 1 }}
                  disabled={isReadOnly}
                  label={translationService.translate(`ocrService.dialogContent.${rectTypes[0]}.endtime`)}
                />
              </LocalizationProvider>
            </Grid>
          ) : (
            <Grid item xs={11} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 32, flexShrink: 0 }} />
              <TextField
                variant="outlined"
                size="small"
                value={detectionTime}
                InputProps={{ readOnly: true }}
                label={translationService.translate(`ocrService.dialogContent.${rectTypes[0]}.time`)}
                sx={{ width: 250 }}
              />
            </Grid>
          )}
          <Grid item xs={1} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            <LabelingInfo type={rectTypes[0]} />
          </Grid>
          <Grid item xs={11} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {rectTypes.map((rectTypeItem, index) => (
              <React.Fragment key={rectTypeItem}>
                {index > 0 && <Box sx={{ width: 32, flexShrink: 0 }} />}
                <Box
                  component="button"
                  type="button"
                  onClick={() => setRectType(rectTypeItem)}
                  sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    bgcolor: rectType === rectTypeItem ? labelsColors[rectTypeItem] : "grey.400",
                    border: "none",
                    borderRadius: 1,
                    cursor: "pointer",
                    color: "common.white",
                  }}
                />
                <TextField
                  value={rectsInput.get(rectTypeItem) ?? ""}
                  variant="outlined"
                  size="small"
                  label={translationService.translate(`ocrService.dialogContent.${rectTypeItem}.coords`)}
                  InputLabelProps={{ shrink: true, style: { color: labelsColors[rectTypeItem] } }}
                  inputProps={{ readOnly: true }}
                  sx={{ width: 250 }}
                />
              </React.Fragment>
            ))}
          </Grid>
          <Grid item xs={1} />
          <Grid item xs={12}>
            <ImageCanvas
              rects={rects}
              rectType={rectType}
              setRects={setRects}
              imageSrc={screenshot}
              disabled={!detectMainInfo || isReadOnly || showingSavedFrame}
            />
          </Grid>
        </Grid>
      </Dialog>
    );
  }
);
